import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Card, Form, InputGroup, Spinner, Badge, Nav } from 'react-bootstrap';
import { 
    MessageSquare, X, Send, ThumbsUp, ThumbsDown, Search, 
    BookOpen, Building2, Car, HelpCircle, Phone, MessageCircle, 
    Calculator, ChevronDown, ChevronUp, AlertTriangle, Sparkles
} from 'lucide-react';
import { apiClient } from '../api';

// Catálogo base de procedimientos (fallback instantáneo mientras carga la API)
const LOCAL_PROCEDURES = [
    {
        id: "crear_viaje",
        category: "Despacho & Sala de Control",
        title: "¿Cómo crear un nuevo servicio o viaje?",
        short_desc: "Pasos para registrar un servicio en la Sala de Control con direcciones, cliente, tipo de vehículo y vuelo.",
        keywords: ["crear viaje", "nuevo servicio", "nuevo viaje", "ingresar viaje", "agendar", "flight", "vuelo", "pasajero", "reserva"],
        steps: [
            "Ve a la pantalla principal de Sala de Control (/transport).",
            "Haz clic en el botón superior '🆕 Nuevo Viaje' para desplegar la ventana modal.",
            "Selecciona el Cliente / Convenio Corporativo (ej. Godrej, Travel Mice, Booking) o deja Particulares sin convenio.",
            "Ingresa el nombre, teléfono y correo del Pasajero.",
            "Escribe la dirección de Origen (Recogida) y Destino (Bajada) usando las sugerencias de Google Maps.",
            "Si el traslado es desde o hacia el aeropuerto, ingresa la Aerolínea y el Número de Vuelo (flight_number).",
            "Define la Fecha y Hora de Recogida (hora local del servicio).",
            "En el selector de tarifas con tarjetas, elige la categoría requerida (STANDARD, VAN, MINIBUS) visualizando su precio estimado.",
            "Presiona 'Crear Servicio'. El viaje quedará registrado en estado 'pending' (Pendiente)."
        ],
        tips: [
            "Si el cliente corporativo exige Centro de Costo, asegúrate de seleccionarlo en la ficha antes de guardar.",
            "Al crear el viaje, el sistema despachará automáticamente un correo al solicitante si el cliente tiene activas las notificaciones."
        ]
    },
    {
        id: "asignar_chofer",
        category: "Despacho & Sala de Control",
        title: "¿Cómo asignar o cambiar un conductor/móvil a un viaje?",
        short_desc: "Asignación manual o inteligente de chofer y vehículo a un servicio.",
        keywords: ["asignar chofer", "asignar movil", "cambiar chofer", "conductor", "asignar", "despachar", "smart assignment"],
        steps: [
            "Ubica el servicio en la tabla de la Sala de Control.",
            "En la columna de conductor, haz clic en el ícono de Asignar Conductor (o sobre el nombre actual si vas a reasignar).",
            "En la modal de asignación verás los móviles disponibles clasificados por cercanía GPS y tipo de vehículo.",
            "Selecciona la tarjeta del conductor y móvil.",
            "Haz clic en 'Confirmar Asignación'.",
            "El estado del viaje cambiará a 'assigned' (Asignado) y el conductor lo verá en su portal móvil."
        ],
        tips: [
            "Puedes usar la Asignación Inteligente (Smart Assignment) para que el sistema proponga el móvil más idóneo según geocerca.",
            "Si un móvil cambia de conductor en mitad del turno, el sistema registrará la dupla exacta para pagarle a la persona correspondiente."
        ]
    },
    {
        id: "estados_viaje",
        category: "Despacho & Sala de Control",
        title: "¿Cómo interpretar los estados de un viaje?",
        short_desc: "Ciclo completo de vida de un servicio en EmiGo desde la reserva hasta el cierre.",
        keywords: ["estados", "pending", "assigned", "en route", "arrived", "on board", "completed", "cancelled", "no show"],
        steps: [
            "1. pending (Pendiente): El servicio fue creado pero no tiene chofer ni móvil asignado.",
            "2. assigned (Asignado): Tiene chofer asignado y aparece en su agenda de la app.",
            "3. en_route (En camino): El chofer presionó 'Voy en camino' (se activa GPS cada 30 seg).",
            "4. arrived (Llegada): El conductor llegó al punto de recogida y está esperando.",
            "5. on_board (A bordo): Pasajero a bordo, trayecto en curso hacia el destino.",
            "6. completed (Completado): Pasajero llegó a destino y el viaje fue finalizado.",
            "7. cancelled (Cancelado): El servicio fue anulado por la operadora o cliente.",
            "8. no_show (No se presentó): Conductor esperó el tiempo reglamentario pero el pasajero no llegó."
        ],
        tips: [
            "Durante en_route y on_board, el celular del chofer mantiene la pantalla fija activa (WakeLock) para no suspender el GPS."
        ]
    },
    {
        id: "anular_viaje",
        category: "Despacho & Sala de Control",
        title: "¿Cómo anular o cancelar un servicio?",
        short_desc: "Protocolo para cancelar un viaje y envío de aviso por correo.",
        keywords: ["anular", "cancelar", "eliminar viaje", "cancelacion", "correo cancelacion"],
        steps: [
            "En la Sala de Control, busca el viaje por ID, pasajero o fecha.",
            "Abre el menú de opciones del viaje o ingresa a editar.",
            "Cambia el estado a 'Cancelado' (cancelled) o presiona 'Anular Servicio'.",
            "Indica el motivo de cancelación si el sistema lo solicita.",
            "Guarda los cambios."
        ],
        tips: [
            "Se enviará automáticamente un correo electrónico de aviso de cancelación al solicitante si el cliente tiene notificaciones activas.",
            "Si el conductor ya llegó al origen, evalúa si corresponde marcarlo como 'no_show' para registrar el cobro correspondiente."
        ]
    },
    {
        id: "agenda_manana",
        category: "Automatizaciones & Agenda",
        title: "¿Cómo funciona el envío de Agenda a Conductores (12:00 y 17:00)?",
        short_desc: "Automatización diaria a las 12:00 y 17:00 (Chile) y envío manual por WhatsApp.",
        keywords: ["agenda", "agenda de mañana", "itinerario", "whatsapp", "12:00", "17:00", "recordatorio", "confirmar"],
        steps: [
            "Automatización de las 12:00 (Ronda General): El servidor envía automáticamente por WhatsApp la hoja de ruta a todos los conductores con viajes asignados para mañana.",
            "Automatización de las 17:00 (Barrido Inteligente): Envía recordatorio a los conductores que aún no han confirmado, y envía la agenda a nuevos viajes creados o reasignados en la tarde.",
            "Envío Manual: En WhatsApp Chat Center (/transport) puedes presionar en cualquier momento el botón 'Enviar Agenda de Mañana'.",
            "En el panel verás el estado de confirmación (verde = confirmado, amarillo = pendiente de lectura)."
        ],
        tips: [
            "A los conductores que ya confirmaron a las 12:00 no se les envía el recordatorio a las 17:00 a menos que tengan un viaje nuevo."
        ]
    },
    {
        id: "caminito_gps",
        category: "Auditoría & GPS",
        title: "¿Cómo ver el trazado de ruta ('El Caminito') y auditar un viaje?",
        short_desc: "Visualización del recorrido real transmitido cada 30 segundos por el conductor.",
        keywords: ["caminito", "gps", "auditoria", "ruta", "recorrido", "trazado", "mapa", "velocidad"],
        steps: [
            "En la Sala de Control, localiza el viaje.",
            "Haz clic en el botón de Auditoría / Ver Ruta (TripAuditModal).",
            "En el mapa verás la línea continua (el caminito) que une los puntos GPS reportados.",
            "Inspecciona la hora exacta, velocidad y detenciones pasando el cursor sobre los puntos.",
            "Compara la ruta real con la óptima para verificar desvíos o justificar cobros."
        ],
        tips: [
            "El conductor reporta su ubicación cada 30 segundos de forma continua mientras está en ruta o a bordo."
        ]
    },
    {
        id: "asignar_zona_grupo",
        category: "Tarifas & Zonas",
        title: "¿Cómo asignar una zona a un Grupo de Zonas? (¡Punto Crítico!)",
        short_desc: "El procedimiento exacto para agrupar polígonos sin cometer errores.",
        keywords: ["zonas", "grupos", "poligono", "editar zona", "grupo de zonas", "asignar grupo"],
        steps: [
            "NO intentes agregar zonas desde la modal de Grupos (esa modal solo crea el nombre del grupo).",
            "En el panel lateral de Zonas, haz clic sobre la Zona individual que deseas clasificar.",
            "Presiona el botón amarillo '✏️ Editar Zona'.",
            "En el desplegable 'Grupo de Zonas', elige el grupo correspondiente.",
            "Presiona '💾 Guardar Cambios'.",
            "Repite este paso para cada comuna que compone el grupo."
        ],
        tips: [
            "Agrupar zonas permite aplicar una sola tarifa a múltiples comunas de forma automática."
        ]
    },
    {
        id: "caso_godrej",
        category: "Clientes & Cobros",
        title: "¿Cómo funciona el reporte especial de Godrej ('Resumen de Cobros')?",
        short_desc: "Exportación de prefactura con pestaña especial por Área y Centro de Costo.",
        keywords: ["godrej", "prefactura", "resumen de cobros", "centro de costo", "area", "excel"],
        steps: [
            "Ingresa al módulo de Prefacturación / Facturación de Clientes (ClientBillingView).",
            "Filtra por el cliente Godrej y selecciona el rango de fechas.",
            "Haz clic en 'Exportar a Excel'.",
            "El sistema detecta automáticamente que es Godrej y crea en la primera posición (index=0) la pestaña 'Resumen de Cobros'.",
            "Esta hoja agrupa por Área, Centro de Costo y Pasajero sumando montos con fórmulas SUM nativas.",
            "Para los demás clientes se genera el formato estándar sin esta pestaña inicial."
        ],
        tips: [
            "Verifica antes de exportar que los servicios de Godrej tengan su Centro de Costo asignado."
        ]
    },
    {
        id: "liquidaciones_movil_chofer",
        category: "Liquidaciones & Pagos",
        title: "¿Cómo liquidar servicios cuando un vehículo cambia de chofer?",
        short_desc: "Separación de pagos entre móvil y chofer en el módulo de liquidaciones.",
        keywords: ["liquidacion", "pago chofer", "cambio de chofer", "dupla", "movil", "anticipos"],
        steps: [
            "EmiGo guarda una foto exacta (snapshot) de qué móvil y qué conductor hizo cada viaje.",
            "Si un vehículo fue conducido por choferes distintos en la semana, no necesitas calcular a mano.",
            "En Liquidaciones (SettlementsView), el sistema agrupa por la dupla real (Conductor + Móvil).",
            "Calcula las ganancias de cada uno por sus días respectivos de forma 100% independiente.",
            "Revisa el balance, descuenta anticipos y presiona 'Cerrar Liquidación'."
        ],
        tips: [
            "Al cerrar la liquidación, el chofer recibe una notificación push en su teléfono con su colilla en PDF."
        ]
    },
    {
        id: "tipos_vehiculos",
        category: "Tarifas & Zonas",
        title: "¿Cuáles son los tipos de vehículos, capacidades de pasajeros y maletas?",
        short_desc: "Capacidades oficiales de la flota en EmiGo.",
        keywords: ["vehiculos", "capacidad", "maletas", "pax", "standard", "van", "sprinter", "minibus"],
        steps: [
            "STANDARD: 2 pasajeros y 2 maletas (Sedán / Station Wagon).",
            "EXECUTIVE / LUXURY: 3 pasajeros y 3 maletas.",
            "PEOPLE_CARRIER: 5 pasajeros y 4 maletas (SUV / Monovolumen).",
            "LARGE_PEOPLE_CARRIER: 6 pasajeros y 4 maletas.",
            "MINIBUS: 10 pasajeros y 10 maletas.",
            "Sprinter: 21 pasajeros y 16 maletas.",
            "BUS: 44 pasajeros y 40 maletas."
        ],
        tips: [
            "El selector de viajes no permite elegir un vehículo si el número de pasajeros o maletas excede su capacidad."
        ]
    }
];

const AssistantChatWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('procedures'); // 'procedures' | 'tariffs' | 'fleet' | 'chat'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [expandedProcedureId, setExpandedProcedureId] = useState('crear_viaje');

    // Datos del backend
    const [procedures, setProcedures] = useState(LOCAL_PROCEDURES);
    const [quickData, setQuickData] = useState({ clientes: [], flota: [] });
    const [isLoadingData, setIsLoadingData] = useState(false);

    // Estado del Cotizador Rápido
    const [quoteOrigin, setQuoteOrigin] = useState('');
    const [quoteDest, setQuoteDest] = useState('');
    const [quoteClient, setQuoteClient] = useState('');
    const [quoteVehicleType, setQuoteVehicleType] = useState('STANDARD');
    const [isCalculatingRate, setIsCalculatingRate] = useState(false);
    const [quoteResult, setQuoteResult] = useState(null);
    const [quoteError, setQuoteError] = useState(null);

    // Estado del Chat Tradicional
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isSendingChat, setIsSendingChat] = useState(false);
    const messagesEndRef = useRef(null);

    // Cargar datos operativos y catálogo al abrir
    useEffect(() => {
        if (isOpen) {
            loadInitialData();
            loadHistory();
        }
    }, [isOpen]);

    // Auto-scroll en chat
    useEffect(() => {
        if (activeTab === 'chat' && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isSendingChat, activeTab]);

    const loadInitialData = async () => {
        setIsLoadingData(true);
        try {
            // Procedimientos
            const procRes = await apiClient.get('/transport/assistant/procedures');
            if (procRes.data && Array.isArray(procRes.data) && procRes.data.length > 0) {
                setProcedures(procRes.data);
            }
        } catch (e) {
            console.warn("Usando catálogo local de procedimientos (fallback):", e);
        }

        try {
            // Clientes y Flota
            const dataRes = await apiClient.get('/transport/assistant/quick-data');
            if (dataRes.data) {
                setQuickData(dataRes.data);
            }
        } catch (e) {
            console.error("Error al cargar datos rápidos de clientes y flota:", e);
        } finally {
            setIsLoadingData(false);
        }
    };

    const loadHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const response = await apiClient.get('/transport/assistant/history');
            const formatted = response.data.map(chat => ({
                id: chat.id,
                query: chat.query,
                response: chat.response,
                rating: chat.rating
            }));
            setMessages(formatted);
        } catch (error) {
            console.error("Error cargando historial de chat:", error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleSendChat = async (e) => {
        e?.preventDefault();
        if (!chatInput.trim() || isSendingChat) return;

        const userMsg = chatInput.trim();
        setChatInput('');
        setIsSendingChat(true);

        const tempMsg = { id: Date.now(), query: userMsg, response: '', isTemp: true };
        setMessages(prev => [...prev, tempMsg]);

        try {
            const response = await apiClient.post('/transport/assistant/chat', { message: userMsg });
            setMessages(prev =>
                prev.map(m =>
                    m.isTemp && m.query === userMsg
                        ? { id: response.data.id, query: response.data.query, response: response.data.response, rating: null }
                        : m
                )
            );
        } catch (error) {
            console.error("Error al enviar mensaje a Emilio:", error);
            setMessages(prev =>
                prev.map(m =>
                    m.isTemp && m.query === userMsg
                        ? {
                              id: Date.now(),
                              query: userMsg,
                              response: "Lo siento, ocurrió un error de conexión con Emilio. Por favor, reintenta en unos momentos.",
                              rating: null
                          }
                        : m
                )
            );
        } finally {
            setIsSendingChat(false);
        }
    };

    const handleFeedback = async (chatId, ratingValue) => {
        try {
            await apiClient.post(`/transport/assistant/feedback/${chatId}`, { rating: ratingValue });
            setMessages(prev =>
                prev.map(m => (m.id === chatId ? { ...m, rating: ratingValue } : m))
            );
        } catch (error) {
            console.error("Error enviando feedback:", error);
        }
    };

    const handleCalculateRate = async (e) => {
        e.preventDefault();
        if (!quoteOrigin.trim() || !quoteDest.trim()) {
            setQuoteError("Por favor ingresa origen y destino.");
            return;
        }

        setIsCalculatingRate(true);
        setQuoteError(null);
        setQuoteResult(null);

        try {
            const payload = {
                origen: quoteOrigin.trim(),
                destino: quoteDest.trim(),
                tipo_vehiculo: quoteVehicleType,
                cliente_id: quoteClient ? parseInt(quoteClient, 10) : undefined
            };
            const response = await apiClient.post('/transport/assistant/calculate-rate', payload);
            if (response.data.error) {
                setQuoteError(response.data.error);
            } else {
                setQuoteResult(response.data);
            }
        } catch (error) {
            console.error("Error al cotizar:", error);
            setQuoteError(error.response?.data?.detail || "No se pudo calcular la tarifa. Verifica las direcciones.");
        } finally {
            setIsCalculatingRate(false);
        }
    };

    // Categorías únicas
    const categories = useMemo(() => {
        const set = new Set(procedures.map(p => p.category));
        return ['all', ...Array.from(set)];
    }, [procedures]);

    // Filtrado de procedimientos
    const filteredProcedures = useMemo(() => {
        return procedures.filter(p => {
            const matchCategory = selectedCategory === 'all' || p.category === selectedCategory;
            if (!matchCategory) return false;

            if (!searchTerm.trim()) return true;
            const term = searchTerm.toLowerCase();
            const inTitle = p.title.toLowerCase().includes(term);
            const inDesc = p.short_desc.toLowerCase().includes(term);
            const inKeywords = p.keywords && p.keywords.some(k => k.toLowerCase().includes(term));
            return inTitle || inDesc || inKeywords;
        });
    }, [procedures, selectedCategory, searchTerm]);

    // Filtrado de clientes
    const filteredClients = useMemo(() => {
        if (!searchTerm.trim()) return quickData.clientes || [];
        const term = searchTerm.toLowerCase();
        return (quickData.clientes || []).filter(c =>
            c.name.toLowerCase().includes(term) ||
            c.rut.toLowerCase().includes(term) ||
            c.segmentation.toLowerCase().includes(term)
        );
    }, [quickData.clientes, searchTerm]);

    // Filtrado de flota
    const filteredFleet = useMemo(() => {
        if (!searchTerm.trim()) return quickData.flota || [];
        const term = searchTerm.toLowerCase();
        return (quickData.flota || []).filter(v =>
            v.plate.toLowerCase().includes(term) ||
            v.driver_name.toLowerCase().includes(term) ||
            v.type.toLowerCase().includes(term) ||
            v.make.toLowerCase().includes(term) ||
            v.model.toLowerCase().includes(term)
        );
    }, [quickData.flota, searchTerm]);

    // Formatear pesos chilenos
    const formatCLP = (num) => {
        if (num === null || num === undefined) return '$0';
        return `$${Math.round(num).toLocaleString('es-CL')}`;
    };

    return (
        <div style={{ position: 'fixed', top: '16px', left: '16px', zIndex: 1050 }}>
            {/* Ventana de Emilio */}
            {isOpen && (
                <Card 
                    className="shadow-2xl border-0" 
                    style={{ 
                        width: '460px', 
                        maxWidth: 'calc(100vw - 32px)',
                        height: '620px', 
                        maxHeight: 'calc(100vh - 80px)',
                        top: '56px', 
                        left: '0', 
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)'
                    }}
                >
                    {/* Header Principal */}
                    <div 
                        className="text-white p-3 d-flex justify-content-between align-items-center" 
                        style={{ background: 'linear-gradient(135deg, #0F2D4A 0%, #1F4E78 100%)' }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <div className="position-relative">
                                <div 
                                    className="rounded-circle bg-success" 
                                    style={{ width: '10px', height: '10px', boxShadow: '0 0 8px #22c55e' }}
                                />
                            </div>
                            <div>
                                <div className="fw-bold fs-6 d-flex align-items-center gap-1">
                                    Emilio - Asistente EmiGo
                                    <Sparkles size={14} className="text-warning" />
                                </div>
                                <div className="small opacity-75" style={{ fontSize: '11px' }}>
                                    Centro Operativo y Manual de Procedimientos
                                </div>
                            </div>
                        </div>
                        <Button 
                            variant="link" 
                            className="text-white p-1 rounded-circle hover-bg-light-10" 
                            onClick={() => setIsOpen(false)}
                            title="Cerrar"
                        >
                            <X size={20} />
                        </Button>
                    </div>

                    {/* Barra de Búsqueda Rápida Unificada */}
                    <div className="p-2 bg-light border-bottom">
                        <InputGroup size="sm">
                            <InputGroup.Text className="bg-white border-end-0 text-muted">
                                <Search size={15} />
                            </InputGroup.Text>
                            <Form.Control 
                                placeholder="Buscar procedimiento, cliente, chofer o patente..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-start-0 ps-1"
                                style={{ boxShadow: 'none' }}
                            />
                            {searchTerm && (
                                <Button 
                                    variant="outline-secondary" 
                                    className="border-start-0 bg-white text-muted"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <X size={14} />
                                </Button>
                            )}
                        </InputGroup>

                        {/* Chips / Accesos Rápidos */}
                        <div className="d-flex gap-1 overflow-auto mt-2 pb-1 no-scrollbar" style={{ whiteSpace: 'nowrap' }}>
                            <Badge 
                                bg="primary" 
                                className="cursor-pointer py-1 px-2 fw-normal" 
                                style={{ fontSize: '11px', cursor: 'pointer' }}
                                onClick={() => { setActiveTab('procedures'); setSearchTerm('crear viaje'); }}
                            >
                                🆕 Crear Viaje
                            </Badge>
                            <Badge 
                                bg="secondary" 
                                className="cursor-pointer py-1 px-2 fw-normal" 
                                style={{ fontSize: '11px', cursor: 'pointer' }}
                                onClick={() => { setActiveTab('procedures'); setSearchTerm('asignar chofer'); }}
                            >
                                👤 Asignar Chofer
                            </Badge>
                            <Badge 
                                bg="warning" 
                                text="dark"
                                className="cursor-pointer py-1 px-2 fw-normal" 
                                style={{ fontSize: '11px', cursor: 'pointer' }}
                                onClick={() => { setActiveTab('procedures'); setSearchTerm('agenda'); }}
                            >
                                📋 Agenda 12:00 / 17:00
                            </Badge>
                            <Badge 
                                bg="info" 
                                className="cursor-pointer py-1 px-2 fw-normal" 
                                style={{ fontSize: '11px', cursor: 'pointer' }}
                                onClick={() => { setActiveTab('procedures'); setSearchTerm('caminito'); }}
                            >
                                🗺️ Caminito GPS
                            </Badge>
                            <Badge 
                                bg="danger" 
                                className="cursor-pointer py-1 px-2 fw-normal" 
                                style={{ fontSize: '11px', cursor: 'pointer' }}
                                onClick={() => { setActiveTab('procedures'); setSearchTerm('anular'); }}
                            >
                                ❌ Anular Viaje
                            </Badge>
                            <Badge 
                                bg="dark" 
                                className="cursor-pointer py-1 px-2 fw-normal" 
                                style={{ fontSize: '11px', cursor: 'pointer' }}
                                onClick={() => { setActiveTab('tariffs'); setSearchTerm('godrej'); }}
                            >
                                🏢 Caso Godrej
                            </Badge>
                        </div>
                    </div>

                    {/* Barra de Pestañas Principales */}
                    <Nav variant="tabs" className="bg-white px-2 pt-1 border-bottom" activeKey={activeTab}>
                        <Nav.Item>
                            <Nav.Link 
                                eventKey="procedures" 
                                onClick={() => setActiveTab('procedures')}
                                className="py-2 px-3 small fw-semibold d-flex align-items-center gap-1"
                                style={{ color: activeTab === 'procedures' ? '#1F4E78' : '#64748b' }}
                            >
                                <BookOpen size={15} />
                                Manual
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                eventKey="tariffs" 
                                onClick={() => setActiveTab('tariffs')}
                                className="py-2 px-3 small fw-semibold d-flex align-items-center gap-1"
                                style={{ color: activeTab === 'tariffs' ? '#1F4E78' : '#64748b' }}
                            >
                                <Building2 size={15} />
                                Convenios & Tarifas
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                eventKey="fleet" 
                                onClick={() => setActiveTab('fleet')}
                                className="py-2 px-3 small fw-semibold d-flex align-items-center gap-1"
                                style={{ color: activeTab === 'fleet' ? '#1F4E78' : '#64748b' }}
                            >
                                <Car size={15} />
                                Flota
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link 
                                eventKey="chat" 
                                onClick={() => setActiveTab('chat')}
                                className="py-2 px-3 small fw-semibold d-flex align-items-center gap-1"
                                style={{ color: activeTab === 'chat' ? '#1F4E78' : '#64748b' }}
                            >
                                <HelpCircle size={15} />
                                Preguntar
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {/* Contenido Principal por Pestaña */}
                    <Card.Body className="p-3 bg-light overflow-y-auto" style={{ flex: 1 }}>
                        {/* PESTAÑA 1: MANUAL DE PROCEDIMIENTOS */}
                        {activeTab === 'procedures' && (
                            <div className="d-flex flex-column gap-2">
                                {/* Filtro de categorías */}
                                <div className="d-flex gap-1 overflow-auto pb-1">
                                    {categories.map(cat => (
                                        <Button
                                            key={cat}
                                            variant={selectedCategory === cat ? 'primary' : 'outline-secondary'}
                                            size="sm"
                                            className="py-0 px-2 text-nowrap"
                                            style={{ 
                                                fontSize: '11px',
                                                backgroundColor: selectedCategory === cat ? '#1F4E78' : 'transparent',
                                                borderColor: selectedCategory === cat ? '#1F4E78' : '#cbd5e1'
                                            }}
                                            onClick={() => setSelectedCategory(cat)}
                                        >
                                            {cat === 'all' ? 'Todos' : cat}
                                        </Button>
                                    ))}
                                </div>

                                {filteredProcedures.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <p className="small mb-1">No se encontraron procedimientos para "{searchTerm}".</p>
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            onClick={() => setSearchTerm('')}
                                            className="p-0 text-primary small"
                                        >
                                            Limpiar búsqueda
                                        </Button>
                                    </div>
                                ) : (
                                    filteredProcedures.map(proc => {
                                        const isExpanded = expandedProcedureId === proc.id;
                                        return (
                                            <Card key={proc.id} className="border shadow-sm mb-1 overflow-hidden">
                                                <div 
                                                    className="p-2 bg-white d-flex justify-content-between align-items-center cursor-pointer"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => setExpandedProcedureId(isExpanded ? null : proc.id)}
                                                >
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <Badge bg="secondary" style={{ fontSize: '9px', fontWeight: 500 }}>
                                                                {proc.category}
                                                            </Badge>
                                                        </div>
                                                        <div className="fw-bold small text-dark">
                                                            {proc.title}
                                                        </div>
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            {proc.short_desc}
                                                        </div>
                                                    </div>
                                                    <div className="text-muted ps-2">
                                                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </div>
                                                </div>

                                                {/* Detalle Desplegable */}
                                                {isExpanded && (
                                                    <div className="p-3 bg-light border-top" style={{ fontSize: '12px' }}>
                                                        <div className="fw-semibold text-primary mb-2">Pasos a seguir:</div>
                                                        <ol className="ps-3 mb-2 text-dark">
                                                            {proc.steps.map((step, idx) => (
                                                                <li key={idx} className="mb-1 leading-snug">
                                                                    {step}
                                                                </li>
                                                            ))}
                                                        </ol>

                                                        {proc.tips && proc.tips.length > 0 && (
                                                            <div className="mt-2 p-2 rounded bg-warning-subtle border border-warning text-dark small" style={{ fontSize: '11px' }}>
                                                                <div className="fw-bold d-flex align-items-center gap-1 mb-1 text-warning-emphasis">
                                                                    <AlertTriangle size={13} />
                                                                    Consejos operativos EmiGo:
                                                                </div>
                                                                <ul className="ps-3 mb-0">
                                                                    {proc.tips.map((tip, idx) => (
                                                                        <li key={idx}>{tip}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Card>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        {/* PESTAÑA 2: CONVENIOS & TARIFAS */}
                        {activeTab === 'tariffs' && (
                            <div className="d-flex flex-column gap-3">
                                {/* Cotizador Rápido de Tarifas Directo */}
                                <Card className="border p-2 bg-white shadow-sm">
                                    <div className="fw-bold small text-dark d-flex align-items-center gap-1 mb-2">
                                        <Calculator size={14} className="text-primary" />
                                        Cotizador Rápido de Tarifa
                                    </div>
                                    <Form onSubmit={handleCalculateRate}>
                                        <div className="d-flex flex-column gap-2 mb-2">
                                            <Form.Control 
                                                size="sm" 
                                                placeholder="Punto de Origen (ej: Las Condes, Aeropuerto)"
                                                value={quoteOrigin}
                                                onChange={(e) => setQuoteOrigin(e.target.value)}
                                            />
                                            <Form.Control 
                                                size="sm" 
                                                placeholder="Punto de Destino (ej: Providencia, Hotel W)"
                                                value={quoteDest}
                                                onChange={(e) => setQuoteDest(e.target.value)}
                                            />
                                            <div className="d-flex gap-2">
                                                <Form.Select 
                                                    size="sm"
                                                    value={quoteClient}
                                                    onChange={(e) => setQuoteClient(e.target.value)}
                                                >
                                                    <option value="">Particular / Sin convenio</option>
                                                    {quickData.clientes.map(c => (
                                                        <option key={c.id} value={c.id}>{c.name}</option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Select 
                                                    size="sm"
                                                    value={quoteVehicleType}
                                                    onChange={(e) => setQuoteVehicleType(e.target.value)}
                                                >
                                                    <option value="STANDARD">STANDARD</option>
                                                    <option value="VAN">VAN / PEOPLE CARRIER</option>
                                                    <option value="MINIBUS">MINIBUS</option>
                                                    <option value="EXECUTIVE">EXECUTIVE</option>
                                                </Form.Select>
                                            </div>
                                        </div>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            type="submit"
                                            className="w-100 fw-semibold"
                                            disabled={isCalculatingRate}
                                            style={{ backgroundColor: '#1F4E78', borderColor: '#1F4E78' }}
                                        >
                                            {isCalculatingRate ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Calculando con motor EmiGo...
                                                </>
                                            ) : (
                                                'Cotizar Tarifa en Vivo'
                                            )}
                                        </Button>
                                    </Form>

                                    {quoteError && (
                                        <div className="alert alert-danger py-1 px-2 mt-2 mb-0 small" style={{ fontSize: '11px' }}>
                                            {quoteError}
                                        </div>
                                    )}

                                    {quoteResult && (
                                        <div className="mt-2 p-2 bg-light border rounded">
                                            <div className="fw-semibold text-success small mb-1">
                                                ✅ Tarifa calculada para {quoteResult.cliente_aplicado}:
                                            </div>
                                            {quoteResult.cotizaciones && quoteResult.cotizaciones.map((q, idx) => (
                                                <div key={idx} className="d-flex justify-content-between align-items-center py-1 border-bottom last:border-0 small">
                                                    <div>
                                                        <strong>{q.tipo_vehiculo}</strong> ({q.capacidad_pax} pax)
                                                        <div className="text-muted" style={{ fontSize: '10px' }}>
                                                            {q.distancia_km ? `${q.distancia_km.toFixed(1)} km` : ''} · {q.duracion_estimada_min ? `~${q.duracion_estimada_min} min` : ''}
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fw-bold text-dark">{formatCLP(q.precio_venta)}</div>
                                                        <div className="text-muted" style={{ fontSize: '10px' }}>Costo: {formatCLP(q.costo_proveedor)}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>

                                {/* Listado de Convenios Corporativos */}
                                <div>
                                    <div className="fw-bold small text-dark mb-2 d-flex justify-content-between align-items-center">
                                        <span>Convenios Registrados ({filteredClients.length})</span>
                                        {isLoadingData && <Spinner animation="border" size="sm" />}
                                    </div>
                                    <div className="d-flex flex-column gap-2">
                                        {filteredClients.map(c => (
                                            <Card key={c.id} className="p-2 border shadow-sm bg-white">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <div className="fw-bold small text-primary">{c.name}</div>
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            RUT: {c.rut} · Segm: {c.segmentation}
                                                        </div>
                                                    </div>
                                                    <Badge bg={c.send_email_notifications ? "success" : "secondary"} style={{ fontSize: '9px' }}>
                                                        {c.send_email_notifications ? "Emails Activos" : "Sin Emails"}
                                                    </Badge>
                                                </div>
                                                <div className="mt-1 d-flex justify-content-between text-muted" style={{ fontSize: '11px' }}>
                                                    <span>📧 {c.contact_email}</span>
                                                    <span>Cond: {c.payment_condition || "30 días"}</span>
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* PESTAÑA 3: FLOTA Y MÓVILES */}
                        {activeTab === 'fleet' && (
                            <div className="d-flex flex-column gap-2">
                                <div className="fw-bold small text-dark d-flex justify-content-between align-items-center">
                                    <span>Flota Registrada ({filteredFleet.length})</span>
                                    {isLoadingData && <Spinner animation="border" size="sm" />}
                                </div>

                                {filteredFleet.length === 0 ? (
                                    <div className="text-center py-4 text-muted small">
                                        No se encontraron vehículos o conductores para "{searchTerm}".
                                    </div>
                                ) : (
                                    filteredFleet.map(v => (
                                        <Card key={v.id} className="p-2 border shadow-sm bg-white">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="badge bg-dark px-2 py-1 font-monospace" style={{ fontSize: '12px' }}>
                                                        {v.plate}
                                                    </span>
                                                    <div>
                                                        <div className="fw-bold small text-dark">
                                                            {v.make} {v.model} ({v.year || "S/A"})
                                                        </div>
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            Color: {v.color || "No esp."} · {v.type} ({v.pax} pax / {v.suitcases} maletas)
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-2 pt-1 border-top d-flex justify-content-between align-items-center" style={{ fontSize: '11px' }}>
                                                <div className="d-flex align-items-center gap-1 text-dark">
                                                    <span className="text-muted">Chofer:</span>
                                                    <strong>{v.driver_name}</strong>
                                                </div>
                                                {v.driver_phone && (
                                                    <div className="d-flex gap-2">
                                                        <a 
                                                            href={`https://wa.me/${v.driver_phone.replace(/[^0-9]/g, '')}`} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-success py-0 px-2 d-flex align-items-center gap-1"
                                                            style={{ fontSize: '11px' }}
                                                        >
                                                            <MessageCircle size={12} />
                                                            WhatsApp
                                                        </a>
                                                        <a 
                                                            href={`tel:${v.driver_phone}`} 
                                                            className="btn btn-sm btn-outline-primary py-0 px-2 d-flex align-items-center gap-1"
                                                            style={{ fontSize: '11px' }}
                                                        >
                                                            <Phone size={12} />
                                                            Llamar
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </Card>
                                    ))
                                )}
                            </div>
                        )}

                        {/* PESTAÑA 4: CHAT CON EMILIO */}
                        {activeTab === 'chat' && (
                            <div className="d-flex flex-column gap-2" style={{ minHeight: '100%' }}>
                                <div className="align-self-start bg-white p-2 rounded shadow-sm border" style={{ maxWidth: '90%' }}>
                                    <p className="mb-0 small fw-bold text-muted" style={{ fontSize: '10px' }}>Emilio</p>
                                    <p className="mb-0 small">
                                        Hola, soy Emilio. Pregúntame lo que necesites sobre el funcionamiento de EmiGo o la operación diaria.
                                    </p>
                                </div>

                                {isLoadingHistory && (
                                    <div className="text-center my-3">
                                        <Spinner animation="border" size="sm" variant="secondary" />
                                    </div>
                                )}

                                {messages.map((msg) => (
                                    <React.Fragment key={msg.id}>
                                        <div className="align-self-end bg-primary text-white p-2 rounded shadow-sm" style={{ maxWidth: '85%' }}>
                                            <p className="mb-0 small">{msg.query}</p>
                                        </div>

                                        {msg.response && (
                                            <div className="align-self-start bg-white p-2 rounded shadow-sm border d-flex flex-column" style={{ maxWidth: '90%' }}>
                                                <p className="mb-1 small fw-bold text-muted" style={{ fontSize: '10px' }}>Emilio</p>
                                                <div className="mb-1 small text-dark" style={{ whiteSpace: 'pre-wrap' }}>{msg.response}</div>
                                                
                                                {!msg.isTemp && (
                                                    <div className="d-flex align-items-center gap-2 mt-1 border-top pt-1 align-self-end">
                                                        <span className="text-muted" style={{ fontSize: '9px' }}>¿Te sirvió?</span>
                                                        <Button 
                                                            variant="link" 
                                                            className={`p-0 ${msg.rating === 1 ? 'text-success' : 'text-muted'}`}
                                                            onClick={() => handleFeedback(msg.id, 1)}
                                                        >
                                                            <ThumbsUp size={12} />
                                                        </Button>
                                                        <Button 
                                                            variant="link" 
                                                            className={`p-0 ${msg.rating === -1 ? 'text-danger' : 'text-muted'}`}
                                                            onClick={() => handleFeedback(msg.id, -1)}
                                                        >
                                                            <ThumbsDown size={12} />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}

                                {isSendingChat && (
                                    <div className="align-self-start bg-white p-2 rounded shadow-sm border" style={{ maxWidth: '85%' }}>
                                        <p className="mb-0 small fw-bold text-muted" style={{ fontSize: '10px' }}>Emilio</p>
                                        <div className="d-flex align-items-center gap-2 py-1">
                                            <Spinner animation="grow" size="sm" variant="secondary" style={{ width: '6px', height: '6px' }} />
                                            <Spinner animation="grow" size="sm" variant="secondary" style={{ width: '6px', height: '6px', animationDelay: '0.2s' }} />
                                            <Spinner animation="grow" size="sm" variant="secondary" style={{ width: '6px', height: '6px', animationDelay: '0.4s' }} />
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </div>
                        )}
                    </Card.Body>

                    {/* Footer con Input si está en pestaña Chat */}
                    {activeTab === 'chat' && (
                        <Card.Footer className="p-2 bg-white border-top">
                            <Form onSubmit={handleSendChat}>
                                <InputGroup size="sm">
                                    <Form.Control
                                        placeholder="Escribe tu consulta a Emilio..."
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        disabled={isSendingChat}
                                    />
                                    <Button 
                                        variant="primary" 
                                        type="submit"
                                        disabled={!chatInput.trim() || isSendingChat}
                                        style={{ backgroundColor: '#1F4E78', borderColor: '#1F4E78' }}
                                    >
                                        <Send size={14} />
                                    </Button>
                                </InputGroup>
                            </Form>
                        </Card.Footer>
                    )}
                </Card>
            )}

            {/* Botón Burbuja Flotante de Emilio */}
            <Button
                onClick={() => setIsOpen(!isOpen)}
                className="rounded-circle shadow-lg d-flex align-items-center justify-content-center p-0"
                style={{ 
                    width: '46px', 
                    height: '46px', 
                    backgroundColor: '#1F4E78', 
                    borderColor: '#0F2D4A',
                    boxShadow: '0 4px 16px rgba(31, 78, 120, 0.35)',
                    transition: 'transform 0.2s ease-in-out'
                }}
                title="Emilio - Asistente EmiGo"
            >
                {isOpen ? <X size={22} color="#fff" /> : <MessageSquare size={22} color="#fff" />}
            </Button>
        </div>
    );
};

export default AssistantChatWidget;
